import { Trash2, Plus, Lock } from "lucide-react";
import { formatINR } from "@/utils/formatINR";
import DecimalInput from "@/components/DecimalInput";

const BASIS_OPTIONS = [
  { value: "process_cost", label: "% of Process Cost (B)" },
  { value: "gross_rmc",    label: "% of Gross RMC" },
  { value: "net_rmc",      label: "% of Net RMC" },
  { value: "sub_total",    label: "% of Sub-Total" },
  { value: "components_total", label: "% of Components Total (Assemblies)" },
  { value: "lump_sum",     label: "₹ Lump Sum" },
];

export default function OverheadsTable({ overheads, onChange, calculations, readOnly, masterMode = false }) {
  const update = (idx, patch) => {
    const next = overheads.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };
  const remove = (idx) => onChange(overheads.filter((_, i) => i !== idx));
  const add = () => onChange([
    ...overheads,
    {
      label: "Custom Overhead",
      value: 0,
      basis: "sub_total",
      is_default: masterMode ? true : false,
      calculated: 0,
    },
  ]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="overheads-table">
      <div className="overflow-x-auto" data-testid="overheads-table-scroll">
        <table className="w-full min-w-[880px] text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left p-2 w-8">#</th>
            <th className="text-left p-2">Item</th>
            <th className="text-left p-2 w-28">Value</th>
            <th className="text-left p-2 w-44">Basis</th>
            {!masterMode && <th className="text-right p-2 w-28">₹/pc</th>}
            <th className="text-left p-2 w-40">Remarks</th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {overheads.map((row, idx) => {
            const calc = calculations?.[idx]?.calculated ?? row.calculated ?? 0;
            const isLumpSum = row.basis === "lump_sum";
            // R32 — auto rows (system-injected, e.g., MOQ carrying) are read-only
            // everywhere in the table; engineer toggles them via include_carrying_in_price.
            const isAuto = !!row.is_auto;
            const labelLocked = !masterMode && (row.is_default || isAuto);
            const rowLocked = readOnly || isAuto;
            const deleteAllowed = !rowLocked && (masterMode || !row.is_default);
            return (
              <tr key={idx} className={`hover:bg-slate-50 ${isAuto ? "bg-orange-50/30" : ""}`} data-testid={isAuto ? `overhead-row-auto-${idx}` : `overhead-row-${idx}`}>
                <td className="p-2 text-slate-400 font-mono text-xs">{idx + 1}</td>
                <td className="p-2">
                  <div className="flex items-center gap-1.5">
                    <input
                      value={row.label}
                      disabled={labelLocked || readOnly}
                      onChange={(e) => update(idx, { label: e.target.value })}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm disabled:bg-slate-50 disabled:text-slate-700 disabled:border-transparent"
                    />
                    {!masterMode && row.is_default && !isAuto && <Lock size={11} className="text-slate-300 flex-shrink-0" />}
                    {isAuto && (
                      <span
                        className="text-[10px] uppercase bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                        data-testid={`overhead-auto-badge-${idx}`}
                        title="System-injected from MOQ carrying-cost setting. Toggle via Section A → Sourcing block."
                      >
                        auto
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-1 relative">
                    {isLumpSum && <span className="text-xs text-slate-400 absolute left-2 pointer-events-none">₹</span>}
                    <DecimalInput
                      disabled={rowLocked}
                      value={isLumpSum ? row.value : Number(((row.value || 0) * 100).toFixed(6))}
                      onChange={(num) => update(idx, { value: isLumpSum ? num : num / 100 })}
                      className={`w-full ${isLumpSum ? "pl-5" : "pr-6"} px-2 py-1 border border-slate-200 rounded text-sm text-right font-mono`}
                    />
                    {!isLumpSum && <span className="text-xs text-slate-400 absolute right-2 pointer-events-none">%</span>}
                  </div>
                </td>
                <td className="p-2">
                  <select
                    value={row.basis}
                    disabled={rowLocked}
                    onChange={(e) => update(idx, { basis: e.target.value })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                  >
                    {BASIS_OPTIONS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </td>
                {!masterMode && (
                  <td className="p-2 text-right font-mono text-sm text-slate-900">{formatINR(calc)}</td>
                )}
                <td className="p-2">
                  <input
                    value={row.remarks || ""}
                    disabled={rowLocked}
                    placeholder="Optional"
                    onChange={(e) => update(idx, { remarks: e.target.value })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm disabled:bg-slate-50 disabled:text-slate-600"
                    data-testid={`overhead-remarks-${idx}`}
                  />
                </td>
                <td className="p-2">
                  {deleteAllowed && (
                    <button type="button" onClick={() => remove(idx)} className="text-slate-300 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="border-t border-slate-200 p-2 flex justify-between items-center bg-slate-50">
        {!readOnly ? (
          <button type="button" onClick={add} className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1">
            <Plus size={14} /> Add {masterMode ? "default" : "custom"} overhead
          </button>
        ) : <div />}
        {!masterMode && (
          <div className="text-sm">
            <span className="text-slate-500">Total Overheads (E):</span>{" "}
            <strong className="font-mono text-slate-900">
              {formatINR((calculations || overheads || []).reduce((s, c) => s + (c.calculated || 0), 0))}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}
